import { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { ApexOptions } from 'apexcharts';

import { BillingPeriodUsage } from 'services/repositories/billing/billing';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const chartOptions: ApexOptions = {
  chart: {
    fontFamily: 'Inter, Helvetica, Arial, sans-serif',
    toolbar: { show: false },
    animations: { enabled: false },
    sparkline: { enabled: false },
  },
  colors: ['#34D399'],
  plotOptions: {
    bar: {
      columnWidth: '40%',
      borderRadius: 2,
    },
  },
  dataLabels: { enabled: false },
  grid: {
    borderColor: '#E5E7EB',
    strokeDashArray: 4,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
  },
  legend: { show: false },
  xaxis: {
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: { style: { colors: '#6B7280', fontSize: '12px' } },
  },
  yaxis: {
    labels: { style: { colors: '#6B7280', fontSize: '12px' } },
  },
  tooltip: {
    y: {
      formatter: (value: number) => `${value}`,
    },
  },
};

type UsageHistoryChartProps = {
  history: Array<BillingPeriodUsage>;
};

export const UsageHistoryChart = ({ history }: UsageHistoryChartProps) => {
  const { t } = useTranslation();

  const series = useMemo(
    () => [
      {
        name: t('settings:usageAndModules.activeModules.usageHistory'),
        data: history.map((entry) => Math.round(entry.credits_used)),
      },
    ],
    [history, t],
  );

  const options = useMemo<ApexOptions>(
    () => ({
      ...chartOptions,
      xaxis: {
        ...chartOptions.xaxis,
        categories: history.map((entry) => formatPeriodLabel(entry.period)),
      },
    }),
    [history],
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900">
          {t('settings:usageAndModules.activeModules.usageHistory')}
        </h4>
      </div>
      <Chart options={options} series={series} type="bar" height={220} />
    </div>
  );
};

const formatPeriodLabel = (period: string) => {
  const [monthStr] = period.split('-');
  const monthIndex = parseInt(monthStr, 10) - 1;
  return MONTH_LABELS[monthIndex] ?? period;
};
